"use client";

import { useState } from "react";

import { api } from "@/lib/api/client";
import type { MonitoringStatus, RobotsVersion, RobotsVersionInfo } from "@/lib/api/types";
import { formatCount, formatDateTime } from "@/lib/format";
import { DIFF_PREFIX, ROBOTS_CLASS, ROBOTS_LABEL } from "@/lib/monitoring";

const ROW_CLASS: Record<string, string> = {
  "+": "bg-success-bg text-[#1e4a30]",
  "-": "bg-danger-bg text-[#7a1e14]",
  " ": "text-[#4a525b]",
  "…": "text-muted italic",
};

/** robots.txt-Karte: Diff der letzten Änderung mit Auswirkung je Bot, dazu die Liste aller Versionen. */
export function RobotsCard({ status, projectId }: { status: MonitoringStatus; projectId: number }) {
  const [list, setList] = useState<RobotsVersionInfo[] | null>(null);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<RobotsVersion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shown = picked ?? status.version;
  const failure = status.last_check?.robots_error ?? null;

  const toggle = async () => {
    setOpen((current) => !current);
    if (list !== null) return;
    try {
      setList(await api<RobotsVersionInfo[]>(`/api/monitoring/robots/versions?project_id=${projectId}`));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    }
  };

  const pick = async (id: number) => {
    if (id === status.version?.id) {
      setPicked(null);
      return;
    }
    try {
      setPicked(await api<RobotsVersion>(`/api/monitoring/robots/versions/${id}?project_id=${projectId}`));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    }
  };

  const title = !shown
    ? "robots.txt"
    : shown.previous_id !== null
      ? `robots.txt · Änderung vom ${formatDateTime(shown.first_seen_at)}`
      : `robots.txt · Stand vom ${formatDateTime(shown.first_seen_at)}`;

  return (
    <section aria-labelledby="robots-title" className="min-w-0 flex-[1_1_380px] rounded-lg border border-line bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#e6e9e7] px-5 py-4">
        <h2 id="robots-title" className="text-base font-semibold">
          {title}
        </h2>
        <button
          type="button"
          onClick={() => void toggle()}
          aria-expanded={open}
          className="inline-flex min-h-11 items-center text-[13px] text-accent underline"
        >
          Versionen ({formatCount(status.version_count)})
        </button>
      </div>

      {failure && (
        <p role="alert" className="border-b border-neutral-bg bg-danger-bg px-5 py-3 text-sm text-danger">
          {failure}. Angezeigt wird der letzte bekannte Stand.
        </p>
      )}
      {error && (
        <p role="alert" className="px-5 py-3 text-sm text-danger">
          {error}
        </p>
      )}

      {open && (
        <ul aria-label="Versionen der robots.txt" className="max-h-60 overflow-y-auto border-b border-neutral-bg">
          {list === null && !error && <li className="px-5 py-3 text-sm text-muted">Versionen werden geladen …</li>}
          {(list ?? []).map((version) => {
            const on = (picked?.id ?? status.version?.id) === version.id;
            return (
              <li key={version.id} className="border-t border-neutral-bg first:border-t-0">
                <button
                  type="button"
                  onClick={() => void pick(version.id)}
                  aria-current={on ? "true" : undefined}
                  className={`flex min-h-11 w-full flex-wrap items-center gap-x-4 gap-y-1 px-5 py-2 text-left text-sm ${
                    on ? "bg-[#eef3fa] shadow-[inset_3px_0_0_#1f5fad]" : "bg-white"
                  }`}
                >
                  <span className="font-medium">{formatDateTime(version.first_seen_at)}</span>
                  <span className="font-mono text-xs text-muted">
                    +{version.added} −{version.removed} Zeilen
                  </span>
                  <span className="text-xs text-muted">
                    {formatCount(version.size)} Bytes
                    {version.status_code !== 200 ? ` · HTTP ${version.status_code} (keine robots.txt)` : ""}
                  </span>
                  <span className="text-xs text-muted">zuletzt gesehen {formatDateTime(version.last_seen_at)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!shown ? (
        <p className="px-5 py-6 text-sm text-muted">
          Es gibt noch keinen abgerufenen Stand. „Jetzt prüfen“ ruft {status.robots_url} ab.
        </p>
      ) : shown.previous_id === null ? (
        <>
          <p className="px-5 pt-3 text-xs text-muted">
            Erster erfasster Stand, es gibt keine Vorversion zum Vergleich.
            {shown.status_code !== 200 ? ` Die Seite hat keine robots.txt (HTTP ${shown.status_code}).` : ""}
          </p>
          <pre className="max-h-80 overflow-auto px-5 py-3 font-mono text-[13px] leading-[1.7]" aria-label="Inhalt">
            {shown.content || "(leer)"}
          </pre>
        </>
      ) : (
        <div className="max-h-80 overflow-auto py-3.5 font-mono text-[13px] leading-[1.7]" role="group" aria-label="Änderungen">
          {shown.diff.length === 0 ? (
            <div className="px-5 text-muted">Keine Änderung zur Vorversion.</div>
          ) : (
            shown.diff.map((row, index) => (
              <div key={index} className={`whitespace-pre px-5 ${ROW_CLASS[row.kind]}`}>
                <span aria-label={{ "+": "hinzugefügt", "-": "entfernt", " ": "unverändert", "…": "ausgelassen" }[row.kind]}>
                  {DIFF_PREFIX[row.kind]}
                </span>
                {row.text}
              </div>
            ))
          )}
        </div>
      )}

      {shown && shown.changes.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-neutral-bg px-5 py-3 text-[13px] text-[#4a525b]">
          <strong className="font-medium text-ink">Auswirkung</strong>
          <ul className="flex flex-col gap-1.5">
            {shown.changes.map((change) => (
              <li key={change.token} className="flex flex-wrap items-start gap-2">
                <span
                  className={`flex-none rounded px-2 py-0.5 text-xs font-medium ${ROBOTS_CLASS[change.after]}`}
                >
                  {change.token}: {ROBOTS_LABEL[change.before]} → {ROBOTS_LABEL[change.after]}
                </span>
                <span className="min-w-0 flex-1">{change.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {shown && shown.sitemaps.length > 0 && (
        <p className="border-t border-neutral-bg px-5 py-2.5 text-xs text-muted">
          Sitemap: {shown.sitemaps.join(", ")}
        </p>
      )}
    </section>
  );
}
